/**
 * API Service
 * 
 * Handles all HTTP requests to the backend
 * Similar to using Guzzle or Laravel's HTTP client
 */

import axios from 'axios';

const getDefaultApiUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname || 'localhost';
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isPrivateLan =
      /^10\./.test(hostname) ||
      /^192\.168\./.test(hostname) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
    const protocol = (isLocal || isPrivateLan) ? 'http:' : (window.location.protocol || 'http:');
    const origin = window.location.origin || `${protocol}//${window.location.host}`;
    const port = window.location.port || '';
    const isDevFrontendPort = port === '3000' || port === '3001';
    if (isLocal) {
      // Local dev: backend runs on port 5000
      return `${protocol}//${hostname}:5000/api`;
    }
    if (isPrivateLan && isDevFrontendPort) {
      // LAN dev from phone: frontend usually runs on 3000/3001 while API is 5000 on same PC.
      return `${protocol}//${hostname}:5000/api`;
    }
    // Production: keep the browser origin intact so ports/proxies keep working.
    return `${origin.replace(/\/+$/, '')}/api`;
  }
  return 'http://localhost:5000/api';
};

const resolveApiUrl = () => {
  const defaultUrl = getDefaultApiUrl();
  const envUrl = process.env.REACT_APP_API_URL;

  // If env is set to localhost/127.0.0.1 but we're being accessed from a non-local
  // hostname (mobile on LAN), override to use the detected default (LAN IP) instead.
  if (envUrl && typeof window !== 'undefined' && window.location) {
    const pageHost = window.location.hostname;
    const pageIsLocal = pageHost === 'localhost' || pageHost === '127.0.0.1';
    const envIsLocal = /localhost|127\.0\.0\.1/i.test(envUrl);

    if (!pageIsLocal && envIsLocal) {
      return defaultUrl;
    }
  }

  return envUrl || defaultUrl;
};

const API_URL = resolveApiUrl();
if (typeof window !== 'undefined') {
  window.__WIKIGAME_API_URL = API_URL;
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Refresh-then-retry logic ---
// Only one refresh call is in flight at a time. Requests that fail while a
// refresh is in progress are queued and replayed once the new token arrives.
let _isRefreshing = false;
let _refreshQueue = []; // [{ resolve, reject }]

function _drainQueue(newToken) {
  _refreshQueue.forEach(({ resolve }) => resolve(newToken));
  _refreshQueue = [];
}

function _rejectQueue(err) {
  _refreshQueue.forEach(({ reject }) => reject(err));
  _refreshQueue = [];
}

function _clearAuthAndRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('user');
  // Notify any AuthContext listeners that the session ended
  window.dispatchEvent(new CustomEvent('auth:sessionExpired'));
  window.location.href = '/login?reason=expired';
}

// Handle 401 responses — attempt silent token refresh before giving up
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const data = error.response?.data;

    // Only intercept expired access tokens; don't retry the refresh call itself
    if (
      error.response?.status === 401 &&
      data?.expired &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      // Another refresh is already in flight — queue this request
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }).catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        // withCredentials sends the httpOnly refresh_token cookie automatically
        const refreshRes = await apiClient.post('/auth/refresh');
        const newToken = refreshRes.data.token;

        localStorage.setItem('token', newToken);
        apiClient.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        window.dispatchEvent(new CustomEvent('auth:tokenRefreshed'));

        _drainQueue(newToken);
        _isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshErr) {
        _isRefreshing = false;
        _rejectQueue(refreshErr);
        _clearAuthAndRedirect();
        return Promise.reject(refreshErr);
      }
    }

    // Hard 401 (no expired flag, or the refresh itself failed) — clear and redirect
    if (
      error.response?.status === 401 &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest._retry
    ) {
      console.warn('⚠️ Unexpected 401 response - triggering logout', {
        url: originalRequest.url,
        data: error.response?.data,
        status: error.response?.status
      });
      _clearAuthAndRedirect();
    }

    return Promise.reject(error);
  }
);

/**
 * Auth API calls
 */

export const authAPI = {
  register: (fullName, username, email, phone, password, confirmPassword, referralCode = '', fingerprint = '') =>
    apiClient.post('/auth/register', { full_name: fullName, username, email, phone, password, confirmPassword, referralCode, fingerprint }),

  login: (username, password) =>
    apiClient.post('/auth/login', { username, password }),

  telegramLogin: (initData) =>
    apiClient.post('/auth/telegram-login', { initData }),

  forgotPassword: (email) =>
    apiClient.post('/auth/forgot-password', { email }),

  resetPassword: (token, password, confirmPassword) =>
    apiClient.post('/auth/reset-password', { token, password, confirmPassword }),

  verifyEmail: (token) =>
    apiClient.post('/auth/verify-email', { token }),

  resendVerification: (email) =>
    apiClient.post('/auth/resend-verification', { email }),

  adminLogin: (username, password) =>
    apiClient.post('/auth/admin-login', { username, password }),

  adminTotpVerify: (challengeToken, code) =>
    apiClient.post('/auth/admin-totp-verify', { challengeToken, code }),

  totpStatus: () => apiClient.get('/auth/totp/status'),
  totpSetup: () => apiClient.get('/auth/totp/setup'),
  totpVerifySetup: (code) => apiClient.post('/auth/totp/verify-setup', { code }),
  totpDisable: (code) => apiClient.post('/auth/totp/disable', { code }),

  logout: () =>
    apiClient.post('/auth/logout'),

  refresh: () =>
    apiClient.post('/auth/refresh'),
};

/**
 * User API calls
 */
export const userAPI = {
  getProfile: () => apiClient.get('/users/profile'),

  updateProfile: (profileData) =>
    apiClient.put('/users/profile', profileData),

  changePassword: ({ oldPassword, newPassword, confirmPassword }) =>
    apiClient.post('/users/change-password', { oldPassword, newPassword, confirmPassword }),

  getReferralData: () => apiClient.get('/users/referral-data'),

  getAdminMessages: () => apiClient.get('/users/admin-messages'),

  dismissAdminMessage: (messageId) =>
    apiClient.post(`/users/admin-messages/${encodeURIComponent(messageId)}/dismiss`),

  getUserById: (id) => apiClient.get(`/users/${id}`),

  getLeaderboard: (game = 'overall', limit = 50) =>
    apiClient.get(`/users/leaderboard?game=${game}&limit=${limit}`),
};

export const adminAPI = {
  getStats: () => apiClient.get('/admin/stats'),

  listUsers: ({ limit = 100, offset = 0, search = '' } = {}) =>
    apiClient.get('/admin/users', { params: { limit, offset, search } }),

  listOnlineUsers: () => apiClient.get('/admin/users/online'),
  
  exportUsersCsv: ({ limit = 5000, search = '' } = {}) =>
    apiClient.get('/admin/users/export.csv', {
      params: { limit, search },
      responseType: 'blob'
    }),

  getUserDetails: (userId) =>
    apiClient.get(`/admin/users/${userId}`),

  getUserTransactions: (userId, { page = 1, limit = 50, category = '', search = '' } = {}) =>
    apiClient.get(`/admin/users/${userId}/transactions`, { params: { page, limit, category, search } }),

  listTransactions: ({ limit = 100, offset = 0, userId = null } = {}) =>
    apiClient.get('/admin/transactions', { params: { limit, offset, userId } }),

  exportTransactionsCsv: ({ limit = 2000, userId = null } = {}) =>
    apiClient.get('/admin/transactions/export.csv', {
      params: { limit, userId },
      responseType: 'blob'
    }),

  blockUser: (userId, reason = '') =>
    apiClient.post(`/admin/users/${userId}/block`, { reason }),

  unblockUser: (userId) =>
    apiClient.post(`/admin/users/${userId}/unblock`),

  updateUserRole: (userId, role) =>
    apiClient.post(`/admin/users/${userId}/role`, { role }),

  verifyUserEmail: (userId) =>
    apiClient.post(`/admin/users/${userId}/verify-email`),

  adjustBalance: (userId, delta, reason = '', options = {}) =>
    apiClient.post(`/admin/users/${userId}/adjust-balance`, { delta, reason, ...options }),

  getSettings: () => apiClient.get('/admin/settings'),

  getWithdrawalMode: () => apiClient.get('/admin/settings/withdrawal-mode'),

  updateWithdrawalMode: (withdrawalMode) =>
    apiClient.put('/admin/settings/withdrawal-mode', { withdrawalMode }),

  updateSettings: (payload) => apiClient.put('/admin/settings', payload),

  listMessages: ({ limit = 100 } = {}) =>
    apiClient.get('/admin/messages', { params: { limit } }),

  createMessage: (payload) => apiClient.post('/admin/messages', payload),

  deleteAllMessages: () => apiClient.delete('/admin/messages'),

  deleteMessage: (messageId) => apiClient.delete(`/admin/messages/${encodeURIComponent(messageId)}`),

  reconcileStaleEscrow: async ({ execute = false, minutes = 180, limit = 200, game = '' } = {}) => {
    const payload = { execute, minutes, limit, game };
    try {
      return await apiClient.post('/admin/reconcile/stale-escrow', payload);
    } catch (error) {
      if (error?.response?.status === 404) {
        return apiClient.post('/admin/reconcile-stale-escrow', payload);
      }
      throw error;
    }
  },
};

export const platformAPI = {
  getSettings: () => apiClient.get('/health'),
};

export const paymentAPI = {
  initializeDeposit: (amount, provider = 'nowpayments') =>
    apiClient.post('/payments/deposit/initialize', { amount, provider }),

  getDepositStatus: (reference) =>
    apiClient.get(`/payments/deposit/${encodeURIComponent(reference)}`),

  listMyDeposits: ({ limit = 50, offset = 0, status = '' } = {}) =>
    apiClient.get('/payments/deposit', { params: { limit, offset, status: status || undefined } }),

  requestWithdrawal: ({ amount, walletAddress, network = 'tron', provider = 'nowpayments', bankName, accountNumber, accountName }) =>
    apiClient.post('/payments/withdrawals/request', { amount, walletAddress, network, provider, bankName, accountNumber, accountName }),

  listMyWithdrawals: ({ limit = 50, offset = 0, status = '' } = {}) =>
    apiClient.get('/payments/withdrawals', { params: { limit, offset, status: status || undefined } }),

  listPendingWithdrawals: ({ limit = 100, offset = 0 } = {}) =>
    apiClient.get('/payments/withdrawals/pending', { params: { limit, offset } }),

  approveWithdrawal: (withdrawalId, verificationCode = '') =>
    apiClient.post(`/payments/withdrawals/${encodeURIComponent(withdrawalId)}/approve`, {
      verificationCode: verificationCode || undefined,
    }),

  rejectWithdrawal: (withdrawalId, reason = 'rejected_by_admin') =>
    apiClient.post(`/payments/withdrawals/${encodeURIComponent(withdrawalId)}/reject`, { reason }),

  listAdminDeposits: ({ limit = 100, offset = 0, status = '', provider = '', search = '', userId = null } = {}) =>
    apiClient.get('/payments/admin/deposits', {
      params: { limit, offset, status: status || undefined, provider: provider || undefined, search: search || undefined, userId: userId || undefined }
    }),

  manualAdminDeposit: ({ userId, amountWkp, note = '' }) =>
    apiClient.post('/payments/admin/deposits/manual', { userId, amountWkp, note }),

  updateAdminDepositStatus: (depositId, payload = {}) =>
    apiClient.post(`/payments/admin/deposits/${encodeURIComponent(depositId)}/status`, payload),

  listAdminWithdrawals: ({ limit = 100, offset = 0, status = '', provider = '', search = '', userId = null } = {}) =>
    apiClient.get('/payments/admin/withdrawals', {
      params: { limit, offset, status: status || undefined, provider: provider || undefined, search: search || undefined, userId: userId || undefined }
    }),

  updateAdminWithdrawalStatus: (withdrawalId, payload = {}) =>
    apiClient.post(`/payments/admin/withdrawals/${encodeURIComponent(withdrawalId)}/status`, payload),

  // ── Bank Transfer (Naira) ──────────────────────────────────────────
  getPublicBankSettings: () =>
    apiClient.get('/payments/bank-settings'),

  requestBankTransfer: ({ amountNaira, description }) =>
    apiClient.post('/payments/bank-transfer/request', { amountNaira, description }),

  listMyBankTransfers: () =>
    apiClient.get('/payments/bank-transfer/my-requests'),

  confirmBankTransferPaid: (id) =>
    apiClient.post(`/payments/bank-transfer/my-requests/${encodeURIComponent(id)}/confirm-paid`),

  adminListBankTransfers: ({ status = 'pending', page = 1 } = {}) =>
    apiClient.get('/payments/admin/bank-transfers', { params: { status, page } }),

  adminApproveBankTransfer: (id, { note = '' } = {}) =>
    apiClient.post(`/payments/admin/bank-transfers/${encodeURIComponent(id)}/approve`, { note }),

  adminRejectBankTransfer: (id, { reason = '' } = {}) =>
    apiClient.post(`/payments/admin/bank-transfers/${encodeURIComponent(id)}/reject`, { reason }),

  adminGetBankSettings: () =>
    apiClient.get('/payments/admin/bank-settings'),

  adminUpdateBankSettings: (payload) =>
    apiClient.post('/payments/admin/bank-settings', payload),
};


/**
 * Matchmaking API calls
 */
export const matchmakingAPI = {
  joinQueue: (wager) => apiClient.post('/matchmaking/join', { wager }),

  leaveQueue: () => apiClient.post('/matchmaking/leave'),

  getQueue: () => apiClient.get('/matchmaking/queue'),
};

/**
 * Game API calls
 */
export const gameAPI = {
  getGame: (id) => apiClient.get(`/games/${id}`),

  getUserGames: () => apiClient.get('/games/user/history'),
};

/**
 * Flappy Duel API calls
 */
export const flappyAPI = {
  listOpenMatches: () => apiClient.get('/flappy/matches/open'),

  listChallenges: () => apiClient.get('/flappy/matches/challenges'),

  listOngoingMatches: () => apiClient.get('/flappy/matches/ongoing'),

  listOngoingMatchesAll: () => apiClient.get('/flappy/matches/ongoing-all'),

  createMatch: ({ wager = 0, roundsTotal = 1, durationSeconds = 60, opponentId = null }) =>
    apiClient.post('/flappy/matches', { wager, roundsTotal, durationSeconds, opponentId }),

  joinMatch: (matchId) => apiClient.post(`/flappy/matches/${matchId}/join`),

  acceptMatch: (matchId) => apiClient.post(`/flappy/matches/${matchId}/accept`),

  startMatch: (matchId) => apiClient.post(`/flappy/matches/${matchId}/start`),

  rechallenge: (matchId) => apiClient.post(`/flappy/matches/${matchId}/rechallenge`),

  cancelMatch: (matchId) => apiClient.post(`/flappy/matches/${matchId}/cancel`),

  claimTimeoutWin: (matchId) => apiClient.post(`/flappy/matches/${matchId}/claim-timeout`),

  readyMatch: (matchId) => apiClient.post(`/flappy/matches/${matchId}/ready`),

  getMatch: (matchId) => apiClient.get(`/flappy/matches/${matchId}`),

  getRound: (matchId, roundNumber) => apiClient.get(`/flappy/matches/${matchId}/rounds/${roundNumber}`),

  submitRun: (matchId, roundNumber, { score, endedReason, endedAtMs, flapTimes }) =>
    apiClient.post(`/flappy/matches/${matchId}/rounds/${roundNumber}/run`, {
      score,
      endedReason,
      endedAtMs,
      flapTimes,
    }),
};

/**
 * Reaction Duel API calls
 */
export const reactionAPI = {
  listOpenMatches: () => apiClient.get('/reaction/matches/open'),

  listChallenges: () => apiClient.get('/reaction/matches/challenges'),

  listOngoingMatches: () => apiClient.get('/reaction/matches/ongoing'),

  createMatch: ({ wager = 0, roundsTotal = 1, opponentId = null, gameMode = 'color_confusion', durationMs }) =>
    apiClient.post('/reaction/matches', { wager, roundsTotal, opponentId, gameMode, durationMs }),

  joinMatch: (matchId) => apiClient.post(`/reaction/matches/${matchId}/join`),

  acceptMatch: (matchId) => apiClient.post(`/reaction/matches/${matchId}/accept`),

  startMatch: (matchId) => apiClient.post(`/reaction/matches/${matchId}/start`),

  cancelMatch: (matchId) => apiClient.post(`/reaction/matches/${matchId}/cancel`),

  rematch: (matchId) => apiClient.post(`/reaction/matches/${matchId}/rematch`),

  getMatch: (matchId) => apiClient.get(`/reaction/matches/${matchId}`),

  getRound: (matchId, roundNumber) => apiClient.get(`/reaction/matches/${matchId}/rounds/${roundNumber}`),

  submitRun: (matchId, roundNumber, runPayload = {}) =>
    apiClient.post(`/reaction/matches/${matchId}/rounds/${roundNumber}/run`, runPayload),
};

/**
 * Chess Duel API calls
 */
export const chessAPI = {
  listOpenMatches: (mode = 'friendly') => apiClient.get(`/chess/matches/open?mode=${encodeURIComponent(mode)}`),

  listChallenges: () => apiClient.get('/chess/matches/challenges'),

  listOngoingMatches: () => apiClient.get('/chess/matches/ongoing'),

  listOngoingMatchesAll: () => apiClient.get('/chess/matches/ongoing-all'),

  listHistory: (limit = 50) => apiClient.get(`/chess/matches/history?limit=${limit}`),

  createMatch: ({ mode = 'friendly', wager = 0, timeControlSeconds, opponentId = null }) =>
    apiClient.post('/chess/matches', { mode, wager, timeControlSeconds, opponentId }),

  joinMatch: (matchId) => apiClient.post(`/chess/matches/${matchId}/join`),

  acceptMatch: (matchId) => apiClient.post(`/chess/matches/${matchId}/accept`),
  rematch: (matchId) => apiClient.post(`/chess/matches/${matchId}/rematch`),
  startMatch: (matchId) => apiClient.post(`/chess/matches/${matchId}/start`),

  cancelMatch: (matchId) => apiClient.post(`/chess/matches/${matchId}/cancel`),

  claimTimeout: (matchId) => apiClient.post(`/chess/matches/${matchId}/claim-timeout`),

  resign: (matchId) => apiClient.post(`/chess/matches/${matchId}/resign`),
  abortMatch: (matchId) => apiClient.post(`/chess/matches/${matchId}/abort`),

  getMatch: (matchId) => apiClient.get(`/chess/matches/${matchId}`),

  getMoves: (matchId) => apiClient.get(`/chess/matches/${matchId}/moves`),

  submitMove: (matchId, { from, to, promotion = 'q' }) =>
    apiClient.post(`/chess/matches/${matchId}/move`, { from, to, promotion }),

  offerDraw: (matchId) => apiClient.post(`/chess/matches/${matchId}/offer-draw`),

  acceptDraw: (matchId) => apiClient.post(`/chess/matches/${matchId}/accept-draw`),

  listTournaments: () => apiClient.get('/chess/tournaments'),

  getTournament: (tournamentId) => apiClient.get(`/chess/tournaments/${tournamentId}`),

  createTournament: ({ name, timeControlSeconds, durationMinutes = 30, maxPlayers = 32, entryWager = 0 }) =>
    apiClient.post('/chess/tournaments', { name, timeControlSeconds, durationMinutes, maxPlayers, entryWager }),

  joinTournament: (tournamentId) => apiClient.post(`/chess/tournaments/${tournamentId}/join`),

  startTournament: (tournamentId) => apiClient.post(`/chess/tournaments/${tournamentId}/start`),

  cancelTournament: (tournamentId) => apiClient.delete(`/chess/tournaments/${tournamentId}`),

  seekArenaMatch: (tournamentId) => apiClient.post(`/chess/tournaments/${tournamentId}/seek`),

  unseekArenaMatch: (tournamentId) => apiClient.post(`/chess/tournaments/${tournamentId}/unseek`),

  refreshTournament: (tournamentId) => apiClient.post(`/chess/tournaments/${tournamentId}/refresh`),

  listTournamentMatches: (tournamentId) => apiClient.get(`/chess/tournaments/${tournamentId}/matches`),
};

/**
 * Checkers Duel API calls
 */
export const checkersAPI = {
  listOpenMatches: (mode = 'friendly') => apiClient.get(`/checkers/matches/open?mode=${encodeURIComponent(mode)}`),

  listChallenges: () => apiClient.get('/checkers/matches/challenges'),

  listOngoingMatches: () => apiClient.get('/checkers/matches/ongoing'),

  listOngoingMatchesAll: () => apiClient.get('/checkers/matches/ongoing-all'),

  listHistory: (limit = 50) => apiClient.get(`/checkers/matches/history?limit=${limit}`),

  createMatch: ({ mode = 'friendly', wager = 0, turnTimeoutSeconds, opponentId = null }) =>
    apiClient.post('/checkers/matches', { mode, wager, turnTimeoutSeconds, opponentId }),

  joinMatch: (matchId) => apiClient.post(`/checkers/matches/${matchId}/join`),

  acceptMatch: (matchId) => apiClient.post(`/checkers/matches/${matchId}/accept`),

  startMatch: (matchId) => apiClient.post(`/checkers/matches/${matchId}/start`),

  cancelMatch: (matchId) => apiClient.post(`/checkers/matches/${matchId}/cancel`),

  claimTimeout: (matchId) => apiClient.post(`/checkers/matches/${matchId}/claim-timeout`),

  resign: (matchId) => apiClient.post(`/checkers/matches/${matchId}/resign`),

  getMatch: (matchId) => apiClient.get(`/checkers/matches/${matchId}`),

  getMoves: (matchId) => apiClient.get(`/checkers/matches/${matchId}/moves`),

  submitMove: (matchId, { from, to }) =>
    apiClient.post(`/checkers/matches/${matchId}/move`, { from, to }),
};

/**
 * Draughts (International 10×10) API
 */
export const draughtsAPI = {
  listOpenMatches: (mode = 'friendly') => apiClient.get(`/draughts/matches/open?mode=${encodeURIComponent(mode)}`),

  listChallenges: () => apiClient.get('/draughts/matches/challenges'),

  listOngoingMatches: () => apiClient.get('/draughts/matches/ongoing'),

  listOngoingMatchesAll: () => apiClient.get('/draughts/matches/ongoing-all'),

  listHistory: (limit = 50) => apiClient.get(`/draughts/matches/history?limit=${limit}`),

  createMatch: ({ mode = 'friendly', wager = 0, turnTimeoutSeconds, opponentId = null, botMatch = false, botLevel = null }) =>
    apiClient.post('/draughts/matches', { mode, wager, turnTimeoutSeconds, opponentId, botMatch, botLevel }),

  joinMatch: (matchId) => apiClient.post(`/draughts/matches/${matchId}/join`),

  acceptMatch: (matchId, payload = {}) => apiClient.post(`/draughts/matches/${matchId}/accept`, payload),
  rematch: (matchId) => apiClient.post(`/draughts/matches/${matchId}/rematch`),

  startMatch: (matchId) => apiClient.post(`/draughts/matches/${matchId}/start`),

  cancelMatch: (matchId) => apiClient.post(`/draughts/matches/${matchId}/cancel`),

  claimTimeout: (matchId) => apiClient.post(`/draughts/matches/${matchId}/claim-timeout`),

  resign: (matchId) => apiClient.post(`/draughts/matches/${matchId}/resign`),

  offerDraw: (matchId) => apiClient.post(`/draughts/matches/${matchId}/draw-offer`),

  acceptDraw: (matchId) => apiClient.post(`/draughts/matches/${matchId}/draw-accept`),

  declineDraw: (matchId) => apiClient.post(`/draughts/matches/${matchId}/draw-decline`),

  getMatch: (matchId) => apiClient.get(`/draughts/matches/${matchId}`),

  getMoves: (matchId) => apiClient.get(`/draughts/matches/${matchId}/moves`),

  submitMove: (matchId, { from, to }) =>
    apiClient.post(`/draughts/matches/${matchId}/move`, { from, to }),
};

/**
 * Rewards & Achievements API
 */
export const rewardsAPI = {
  getStreak: () => apiClient.get('/rewards/streak'),
  claimDaily: () => apiClient.post('/rewards/claim'),
  getHistory: (limit = 30) => apiClient.get('/rewards/history', { params: { limit } }),
  getAchievements: () => apiClient.get('/rewards/achievements'),
  getBonusSummary: () => apiClient.get('/rewards/bonus-summary'),
};

export default apiClient;
